import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CaretDown, ArrowUp } from "@phosphor-icons/react";
import { useBuyerTestimonials } from "@/lib/useBuyerTestimonials";
import { useSharedSections } from "@/lib/useSharedSections";
import { fillFirm } from "@/lib/sharedSectionDefaults";
import { testimonialPhoto } from "../BuyerTestimonials";
import { CASE_FAQ, HOW_IT_WORKS } from "./caseContent";

const inView = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { delay: i * 0.08, duration: 0.45 },
});

const SectionTitle = ({ eyebrow, line }) => (
  <div className="max-w-2xl">
    <h2 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--cf-accent)] md:text-lg">{eyebrow}</h2>
    <p className="lp-serif mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">{line}</p>
  </div>
);

export const HowItWorks = () => {
  const steps = useSharedSections().how_it_works || HOW_IT_WORKS;
  return (
  <section className="border-t border-white/10 px-5 py-16 sm:py-24" data-testid="case-how-it-works">
    <div className="mx-auto max-w-6xl">
      <SectionTitle eyebrow="How it works" line="Three taps closer to answers." />
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {steps.map((s, i) => (
          <motion.div key={`${s.title}-${i}`} {...inView(i)} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="lp-serif text-5xl font-bold text-[var(--cf-accent)]">0{i + 1}</div>
            <div className="mt-4 text-lg font-bold text-white">{s.title}</div>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

const CaseTestimonialGrid = ({ items }) => (
  <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="case-testimonials">
    {items.slice(0, 3).map((t, i) => (
      <motion.figure key={t.id} {...inView(i)} className="rounded-2xl border border-black/10 bg-white p-6" data-testid={`case-testimonial-${t.id}`}>
        <div className="text-amber-500" aria-label={`${t.stars} star review`}>{"★".repeat(t.stars)}</div>
        <blockquote className="mt-3 text-[15px] leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
        <figcaption className="mt-4 flex items-center gap-2 text-sm font-bold">
          {testimonialPhoto(t) && <img src={testimonialPhoto(t)} alt="" className="h-9 w-9 rounded-full object-cover" />}
          <span>{t.name}{t.case_tag && <span className="block text-xs font-normal text-slate-500">{t.case_tag}</span>}</span>
        </figcaption>
      </motion.figure>
    ))}
  </div>
);

export const CaseProof = ({ buyerCode }) => {
  const items = useBuyerTestimonials(buyerCode);
  const shared = useSharedSections();
  const has = items.length > 0;
  return (
  <section className="bg-[#f6f3ec] px-5 py-16 text-[#0a1929] sm:py-24" data-testid="case-proof">
    <div className="mx-auto max-w-6xl">
      <h2 className="text-base font-semibold uppercase tracking-[0.2em] md:text-lg">{has ? "Real stories" : "Case results"}</h2>
      <p className="lp-serif mt-3 max-w-2xl text-3xl font-bold leading-tight sm:text-4xl">{has ? <>People who&apos;ve been where you are.</> : "What injury cases like yours have recovered."}</p>
      {has && <CaseTestimonialGrid items={items} />}
      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3">
        {shared.case_results.slice(0, 6).map((r, i) => (
          <div key={`${r.label}-${i}`} className="rounded-xl border border-black/10 bg-white px-4 py-3">
            <div className="lp-serif text-2xl font-bold">{r.amount}</div>
            <div className="mt-1 text-xs text-slate-600">{r.label}</div>
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs text-slate-500">
        {has && "Reviews reflect individual experiences. "}{shared.results_disclaimer}
      </p>
    </div>
  </section>
  );
};

const FaqItem = ({ item, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-white/10">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              className="flex min-h-[56px] w-full items-center justify-between gap-4 py-4 text-left text-base font-semibold text-white"
              data-testid={`case-faq-item-${i}`}>
        {item.q}
        <motion.span animate={{ rotate: open ? 180 : 0 }} className="shrink-0 text-[var(--cf-accent)]"><CaretDown size={18} weight="bold" /></motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pb-4 text-sm leading-relaxed text-slate-400">
            {item.a}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CaseFaq = ({ slug }) => {
  const items = useSharedSections().faq || CASE_FAQ[slug] || [];
  return (
  <section className="px-5 py-16 sm:py-24" data-testid="case-faq">
    <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
      <SectionTitle eyebrow="Questions" line="What people ask us first." />
      <div>{items.map((item, i) => <FaqItem key={`${item.q}-${i}`} item={item} i={i} />)}</div>
    </div>
  </section>
  );
};

export const FinalCta = ({ firmName, ctaText }) => {
  const shared = useSharedSections().final_cta;
  const firm = firmName || "an experienced injury attorney";
  return (
  <section className="px-5 pb-20" data-testid="case-final-cta">
    <motion.div {...inView()} className="cf-soft mx-auto flex max-w-6xl flex-col items-start gap-6 rounded-3xl border border-white/10 p-8 sm:p-12 md:flex-row md:items-center md:justify-between">
      <div>
        <p className="lp-serif text-3xl font-bold leading-tight text-white sm:text-4xl">{shared?.title ? fillFirm(shared.title, firmName, firm) : "Find out if you have a case."}</p>
        <p className="mt-2 text-base text-slate-300">{shared?.body ? fillFirm(shared.body, firmName, firm) : `Talk to ${firm} — free, with no obligation.`}</p>
      </div>
      <button type="button"
              onClick={() => document.getElementById("case-quiz")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="cf-cta inline-flex min-h-[52px] items-center gap-2 rounded-full bg-[var(--cf-accent)] px-7 text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="case-final-cta-btn">
        {shared?.button || ctaText} <ArrowUp size={18} weight="bold" />
      </button>
    </motion.div>
  </section>
  );
};
